import React, { useEffect, useRef, useState } from "react";
import { FiCheckCircle, FiRotateCcw, FiX, FiXCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import { client } from "../../Clients";
import type { BackupJobDto, RestoreAcceptedDto } from "./backupClient";
import { extractApiError, formatDateTime } from "./ui";

/**
 * Vue « Restauration en cours » : POST /restore répond 202 { jobId } (la restauration tourne
 * en tâche de fond côté serveur) et cette modale sonde GET /api/backup/jobs/{jobId} toutes
 * les 2 s jusqu'à l'état terminal : Terminée (toast vert, lignes appliquées), Échec (toast
 * rouge + erreur du job) ou Annulée. L'API n'expose pas les deltas par table dans le JobDto :
 * le récapitulatif s'appuie sur `rowsExported` (lignes appliquées) ; le détail par table reste
 * consultable dans l'audit serveur.
 *
 * Fermer la modale n'interrompt RIEN : la restauration continue côté serveur et reste visible
 * dans le journal des tâches (type « Restore »).
 */

const TERMINAL_STATUSES = new Set(["Completed", "Failed", "Cancelled"]);

const modeLabel = (mode: string): string => (mode.toLowerCase() === "replace" ? "remplacement" : "fusion");

interface RestoreProgressModalProps {
	/** Réponse 202 du POST /restore (jobId + archive + mode). */
	accepted: RestoreAcceptedDto;
	onClose: () => void;
	/** Appelé UNE fois quand le job atteint un état terminal (le parent rafraîchit ses listes). */
	onFinished?: () => void;
	pollIntervalMs?: number;
}

const RestoreProgressModal: React.FC<RestoreProgressModalProps> = ({
	accepted,
	onClose,
	onFinished,
	pollIntervalMs = 2000,
}) => {
	const [job, setJob] = useState<BackupJobDto | null>(null);
	const [pollError, setPollError] = useState<string | null>(null);
	const cancelledRef = useRef(false);
	// Réfs pour ne pas relancer la boucle de sondage quand le parent re-rend.
	const onFinishedRef = useRef(onFinished);
	onFinishedRef.current = onFinished;

	useEffect(() => {
		cancelledRef.current = false;
		(async () => {
			while (!cancelledRef.current) {
				try {
					const j = await client.backup.getJob(accepted.jobId);
					if (cancelledRef.current) return;
					setJob(j);
					setPollError(null);
					if (TERMINAL_STATUSES.has(j.status)) {
						if (j.status === "Completed") {
							toast.success(
								`Restauration de ${accepted.backupId} terminée (mode ${modeLabel(accepted.mode)}) : ` +
									`${j.rowsExported} ligne(s) appliquée(s).`
							);
						} else if (j.status === "Failed") {
							toast.error(`Restauration de ${accepted.backupId} échouée : ${j.error ?? "erreur inconnue"}.`);
						} else {
							toast.error(`Restauration de ${accepted.backupId} annulée.`);
						}
						onFinishedRef.current?.();
						return;
					}
				} catch (e) {
					// Sondage transitoirement en échec : on l'affiche mais on continue : le job,
					// lui, vit sa vie côté serveur.
					if (cancelledRef.current) return;
					setPollError(extractApiError(e));
				}
				await new Promise((r) => setTimeout(r, pollIntervalMs));
			}
		})();
		return () => {
			cancelledRef.current = true;
		};
	}, [accepted.jobId, accepted.backupId, accepted.mode, pollIntervalMs]);

	const status = job?.status ?? "Running";
	const finished = TERMINAL_STATUSES.has(status);
	const failed = status === "Failed" || status === "Cancelled";

	return (
		<div
			className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/50 p-4"
			role="dialog"
			aria-modal="true"
			aria-label="Suivi de la restauration"
		>
			<div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
				<div className="flex items-start justify-between gap-3">
					<div className="flex items-center gap-2">
						<span
							className={[
								"inline-flex h-9 w-9 items-center justify-center rounded-xl",
								failed ? "bg-red-100 text-red-600" : finished ? "bg-emerald-100 text-emerald-600" : "bg-sky-100 text-sky-600",
							].join(" ")}
						>
							{failed ? (
								<FiXCircle className="h-5 w-5" aria-hidden />
							) : finished ? (
								<FiCheckCircle className="h-5 w-5" aria-hidden />
							) : (
								<FiRotateCcw className="h-5 w-5 animate-spin" aria-hidden />
							)}
						</span>
						<h3 className="text-base font-semibold text-slate-800">
							{failed
								? status === "Cancelled"
									? "Restauration annulée"
									: "Échec de la restauration"
								: finished
								? "Restauration terminée"
								: "Restauration en cours"}
						</h3>
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Fermer"
						className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
					>
						<FiX className="h-5 w-5" aria-hidden />
					</button>
				</div>

				<p className="mt-3 text-sm text-slate-600">
					Archive : <code className="rounded bg-slate-100 px-1 text-xs">{accepted.backupId}</code> : mode{" "}
					<span className="font-semibold">{modeLabel(accepted.mode)}</span> : tâche{" "}
					<code className="rounded bg-slate-100 px-1 text-xs">{accepted.jobId}</code>.
				</p>

				{!finished && (
					<>
						<div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-label="Restauration en cours">
							{/* Pas de total connu côté API : barre indéterminée. */}
							<div className="h-full w-1/3 animate-pulse rounded-full bg-sky-500" />
						</div>
						<p className="mt-2 text-xs text-slate-600">
							Démarrée : {formatDateTime(job?.startedAt)} : statut : {status === "Finalizing" ? "finalisation" : "en cours"}.
						</p>
						<p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
							Vous pouvez fermer cette fenêtre : la restauration continue côté serveur et reste visible dans le
							journal des tâches.
						</p>
					</>
				)}

				{finished && !failed && job && (
					<p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
						Restauration terminée : <span className="font-semibold">{job.rowsExported} ligne(s) appliquée(s)</span>
						{job.finishedAt ? ` (achevée le ${formatDateTime(job.finishedAt)})` : ""}. Le détail par table est
						consultable dans l'audit du serveur.
					</p>
				)}

				{failed && (
					<p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
						{status === "Cancelled"
							? "La restauration a été annulée côté serveur."
							: `Échec : ${job?.error ?? "erreur inconnue"}`}
					</p>
				)}

				{pollError && (
					<p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
						Suivi momentanément indisponible ({pollError}) : nouvel essai automatique.
					</p>
				)}

				<div className="mt-4 flex justify-end">
					<button
						type="button"
						onClick={onClose}
						className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
					>
						Fermer
					</button>
				</div>
			</div>
		</div>
	);
};

export default RestoreProgressModal;
